import React, { useMemo } from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import { usePermissoes } from "../../contexts/PermissoesContext.tsx";
import {
  CatalogoPermissoes,
  TipoUsuario,
  permiteFuncionalidade,
} from "../../types/Permissoes.ts";

interface Props {
  tipo: TipoUsuario;
  podeExcluirAgendamento: boolean;
  podeVerLog: boolean;
  /** Funcao atual do usuario sendo editado, para avisar o que ele vai perder. */
  tipoAnterior?: TipoUsuario | null;
  chavesAnteriores?: { podeExcluirAgendamento: boolean; podeVerLog: boolean };
  /** Modo consulta (Configuracoes > Perfil): sem titulo de formulario. */
  somenteLeitura?: boolean;
}

const rotuloFuncao = (catalogo: CatalogoPermissoes | null, tipo: TipoUsuario) =>
  catalogo?.funcoes.find((f) => f.codigo === tipo)?.rotulo ?? tipo;

const PermissoesPreview: React.FC<Props> = ({
  tipo,
  podeExcluirAgendamento,
  podeVerLog,
  tipoAnterior,
  chavesAnteriores,
  somenteLeitura = false,
}) => {
  const { catalogo } = usePermissoes();

  const chaves = useMemo(
    () => ({ podeExcluirAgendamento, podeVerLog }),
    [podeExcluirAgendamento, podeVerLog]
  );

  // O que o usuario PERDE ao salvar. Mostrado so na edicao, e so quando ha
  // perda: e o momento exato em que um erro custa caro, e avisar e barato.
  const perdas = useMemo(() => {
    if (!catalogo || !tipoAnterior || !chavesAnteriores) return [];
    const antes = chavesAnteriores;
    const lista: string[] = [];
    catalogo.grupos.forEach((g) =>
      g.funcionalidades.forEach((f) => {
        const tinha = permiteFuncionalidade(f, tipoAnterior, antes);
        const tera = permiteFuncionalidade(f, tipo, chaves);
        if (tinha && !tera) lista.push(f.rotulo);
      })
    );
    return lista;
  }, [catalogo, tipo, chaves, tipoAnterior, chavesAnteriores]);

  if (!catalogo) return null;

  const conteudo = (
    <>
      {catalogo.grupos.map((grupo) => (
        <div key={grupo.grupo} style={{ marginTop: 14 }}>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: 0.7,
              textTransform: "uppercase",
              color: "var(--ink-faint)",
              marginBottom: 6,
            }}
          >
            {grupo.grupo}
          </div>
          {grupo.funcionalidades.map((f) => {
            const liberado = permiteFuncionalidade(f, tipo, chaves);
            return (
              <div
                key={f.codigo}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "3px 0",
                  fontSize: 12.6,
                  // Negado em cinza, nao em vermelho: e uma descricao do
                  // acesso, nao um erro que o usuario cometeu.
                  color: liberado ? "var(--ink)" : "var(--ink-faint)",
                }}
              >
                {liberado ? (
                  <CheckIcon style={{ fontSize: 15, color: "var(--green)" }} />
                ) : (
                  <CloseIcon style={{ fontSize: 15, opacity: 0.55 }} />
                )}
                <span style={{ flex: 1, minWidth: 0 }}>{f.rotulo}</span>
                {f.chave && (
                  <span
                    title="Depende da permissão individual marcada acima, não da função"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 3,
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: "var(--ink-faint)",
                      border: "1px solid var(--border)",
                      borderRadius: 999,
                      padding: "1px 7px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <TuneIcon style={{ fontSize: 11 }} />
                    permissão
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {perdas.length > 0 && (
        <div
          style={{
            marginTop: 16,
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid var(--orange)",
            background: "var(--orange-tint)",
            fontSize: 12.2,
            lineHeight: 1.5,
          }}
        >
          <strong>Ao salvar, este usuário perde:</strong> {perdas.join(", ")}.
        </div>
      )}
    </>
  );

  return (
    <div
      className="ds-card"
      style={{ padding: 16, marginTop: 14, background: "var(--surface-2)" }}
    >
      <div style={{ fontSize: 13, fontWeight: 800 }}>
        {somenteLeitura
          ? `Sua função: ${rotuloFuncao(catalogo, tipo)}`
          : `O que ${rotuloFuncao(catalogo, tipo)} pode fazer`}
      </div>
      <p style={{ fontSize: 11.8, color: "var(--ink-faint)", margin: "4px 0 0" }}>
        {somenteLeitura
          ? "Estes são os acessos do seu usuário hoje. Para alterar, fale com um Administrador da empresa."
          : "Confira antes de salvar. Itens marcados como permissão dependem das opções acima, não da função."}
      </p>
      {conteudo}
    </div>
  );
};

export default PermissoesPreview;
